/**
 * AdminJobsPage - View recent AI job runs
 *
 * Drives:
 * - GET /api/admin/jobs
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RefreshCw, AlertTriangle, ChevronRight, CheckCircle2, XCircle, Clock, Play, Wrench, X, Search, Eye } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../lib/api';

// Allowed reviewer models (canonical list - dropdown only, no free text)
const ALLOWED_REVIEWER_MODELS = [
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (Default)' },
  { value: 'claude-opus-4-20250514', label: 'Claude Opus 4' },
];

// Allowed tagging models for fix-failed operations
const ALLOWED_TAGGING_MODELS = [
  { value: '', label: 'Use original model' },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (Recommended)' },
  { value: 'claude-opus-4-20250514', label: 'Claude Opus 4 (Higher quality)' },
  { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (Faster/cheaper)' },
];

// Backend response shape from worker/lib/admin-config.js handleGetJobRuns
interface LatestReview {
  id: string;
  finished_at: string | null;
  items_attempted: number;
  items_suggested: number;
  approved_at: string | null;
  applied_at: string | null;
  created_at: string;
}

interface JobRun {
  id: string;
  job_type: string; // ai_facets_v1 | ai_overlay_genres_v1 | ai_microtag_v1
  model: string;
  prompt_version: string;
  items_attempted: number;
  items_succeeded: number;
  items_failed: number;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_usd: number;
  started_at: string;
  finished_at: string | null;
  selector: Record<string, unknown>;
  // Review status fields
  review_status: 'none' | 'pending' | 'changes_suggested' | 'approved' | 'applied' | 'no_changes';
  latest_review: LatestReview | null;
}

interface JobRunsResponse {
  jobs: JobRun[];
  next_cursor: { started_at: string; id: string } | null;
}

// Progress response from /admin/jobs/:id/progress (for live polling)
interface JobProgress {
  job_run_id: string;
  items_attempted: number;
  counts: { queued: number; processing: number; succeeded: number; failed: number; skipped: number };
  done: number;
  remaining: number;
  avg_seconds_per_item: number | null;
  eta_seconds: number | null;
  as_of: string;
}

// Fix failed request/response types
interface FixFailedRequest {
  dry_run: boolean;
  max_items: number;
  model?: string;
  prompt_version?: string;
  only_error_codes?: string[];
}

interface FixFailedPreviewItem {
  media_type: string;
  tmdb_id: number;
  error_code: string | null;
  error_message: string | null;
}

interface FixFailedResponse {
  success: boolean;
  dry_run: boolean;
  parent_job_run_id: string;
  retry_job_run_id: string | null;
  job_type: string;
  model: string;
  prompt_version: string;
  items_selected: number;
  batches_enqueued: number;
  batch_size: number;
  next_cursor: { media_type: string; tmdb_id: number } | null;
  items?: FixFailedPreviewItem[];
  message?: string;
  tag_key?: string;
  tag_prompt?: string;
}

// Review request/response types
interface StartReviewRequest {
  dry_run: boolean;
  max_items: number;
  model: string;
  prompt_version?: string;
}

interface StartReviewResponse {
  success: boolean;
  dry_run: boolean;
  review_id: string | null;
  parent_job_run_id: string;
  target_job_type: string;
  model: string;
  prompt_version: string;
  items_selected: number;
  batches_enqueued: number;
  batch_size: number;
  next_cursor: { media_type: string; tmdb_id: number } | null;
  message?: string;
}

// Helper to get review status badge
function getReviewStatusBadge(status: JobRun['review_status']) {
  switch (status) {
    case 'pending':
      return <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">Reviewing</span>;
    case 'changes_suggested':
      return <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">Changes</span>;
    case 'approved':
      return <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">Approved</span>;
    case 'applied':
      return <span className="text-xs px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">Applied</span>;
    case 'no_changes':
      return <span className="text-xs px-1.5 py-0.5 rounded bg-slate-500/20 text-slate-400">No Changes</span>;
    default:
      return null;
  }
}

// Real job_type values from backend
const JOB_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'ai_facets_v1', label: 'AI Facets' },
  { value: 'ai_overlay_genres_v1', label: 'AI Overlay Genres' },
  { value: 'ai_microtag_v1', label: 'AI Microtag' },
];

// Derive UI status from backend fields
type UiStatus = 'running' | 'has_errors' | 'complete';

function deriveStatus(job: JobRun): UiStatus {
  if (!job.finished_at) return 'running';
  if (job.items_failed > 0) return 'has_errors';
  return 'complete';
}

function getStatusIcon(status: UiStatus) {
  switch (status) {
    case 'complete':
      return <CheckCircle2 size={16} className="text-green-400" />;
    case 'has_errors':
      return <XCircle size={16} className="text-red-400" />;
    case 'running':
      return <Play size={16} className="text-blue-400" />;
    default:
      return <Clock size={16} className="text-slate-400" />;
  }
}

function getStatusColor(status: UiStatus) {
  switch (status) {
    case 'complete':
      return 'text-green-400';
    case 'has_errors':
      return 'text-red-400';
    case 'running':
      return 'text-blue-400';
    default:
      return 'text-slate-400';
  }
}

function getStatusLabel(status: UiStatus): string {
  switch (status) {
    case 'complete':
      return 'Complete';
    case 'has_errors':
      return 'Has Errors';
    case 'running':
      return 'Running';
    default:
      return 'Unknown';
  }
}

function formatJobType(jobType: string): string {
  switch (jobType) {
    case 'ai_facets_v1':
      return 'AI Facets';
    case 'ai_overlay_genres_v1':
      return 'AI Overlay Genres';
    case 'ai_microtag_v1':
      return 'AI Microtag';
    default:
      return jobType;
  }
}

function formatDuration(startedAt: string, finishedAt: string | null): string {
  const start = new Date(startedAt).getTime();
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  const durationMs = end - start;

  if (durationMs < 1000) return '<1s';
  if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
  if (durationMs < 3600000) return `${Math.round(durationMs / 60000)}m`;
  return `${Math.round(durationMs / 3600000)}h`;
}

export function AdminJobsPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [jobType, setJobType] = useState('');
  const [response, setResponse] = useState<JobRunsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<Array<{ started_at: string; id: string }>>([]);
  const limit = 25;

  // Live progress polling for running jobs
  const [progressByJobId, setProgressByJobId] = useState<Record<string, JobProgress>>({});

  // Fix failed modal state
  const [fixFailedJob, setFixFailedJob] = useState<JobRun | null>(null);
  const [fixFailedMaxItems, setFixFailedMaxItems] = useState(200);
  const [fixFailedModel, setFixFailedModel] = useState('');
  const [fixFailedPromptVersion, setFixFailedPromptVersion] = useState('');
  const [fixFailedDryRun, setFixFailedDryRun] = useState(true);
  const [fixFailedLoading, setFixFailedLoading] = useState(false);
  const [fixFailedResult, setFixFailedResult] = useState<FixFailedResponse | null>(null);
  const [fixFailedError, setFixFailedError] = useState<string | null>(null);

  // Review modal state
  const [reviewJob, setReviewJob] = useState<JobRun | null>(null);
  const [reviewMaxItems, setReviewMaxItems] = useState(200);
  const [reviewModel, setReviewModel] = useState(ALLOWED_REVIEWER_MODELS[0].value);
  const [reviewPromptVersion, setReviewPromptVersion] = useState('');
  const [reviewDryRun, setReviewDryRun] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewResult, setReviewResult] = useState<StartReviewResponse | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const fetchJobs = async (cursor?: { started_at: string; id: string }) => {
    if (!session?.access_token) return;

    setLoading(true);
    setError(null);

    try {
      let url = `/admin/jobs?limit=${limit}`;
      if (jobType) url += `&job_type=${jobType}`;
      if (cursor) {
        url += `&cursor_started_at=${encodeURIComponent(cursor.started_at)}&cursor_id=${cursor.id}`;
      }

      const data = await apiFetch<JobRunsResponse>(url, {
        token: session.access_token,
      });
      setResponse(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when job_type changes
  useEffect(() => {
    setCursorStack([]);
    fetchJobs();
  }, [jobType, session?.access_token]);

  // Poll progress for running jobs or jobs with inconsistent rollups every 10 seconds
  useEffect(() => {
    if (!session?.access_token || !response?.jobs) return;

    // Poll jobs that are either running OR have inconsistent rollup counts
    const jobsNeedingProgress = response.jobs.filter((j) =>
      !j.finished_at || (j.items_succeeded + j.items_failed) < j.items_attempted
    ).slice(0, 10);
    if (jobsNeedingProgress.length === 0) return;

    const fetchProgress = async () => {
      const results = await Promise.allSettled(
        jobsNeedingProgress.map((job) =>
          apiFetch<JobProgress>(`/admin/jobs/${job.id}/progress`, {
            token: session.access_token,
          }).then((prog) => ({ id: job.id, progress: prog }))
        )
      );

      const newProgress: Record<string, JobProgress> = {};
      for (const result of results) {
        if (result.status === 'fulfilled') {
          newProgress[result.value.id] = result.value.progress;
        }
      }
      setProgressByJobId((prev) => ({ ...prev, ...newProgress }));
    };

    // Fetch immediately on mount
    fetchProgress();

    // Poll every 10 seconds
    const interval = setInterval(fetchProgress, 10_000);
    return () => clearInterval(interval);
  }, [response?.jobs, session?.access_token]);

  const handleNextPage = () => {
    if (response?.next_cursor) {
      // Push current first item as "back" cursor
      if (response.jobs.length > 0) {
        setCursorStack((prev) => [
          ...prev,
          { started_at: response.jobs[0].started_at, id: response.jobs[0].id },
        ]);
      }
      fetchJobs(response.next_cursor);
    }
  };

  const handlePrevPage = () => {
    if (cursorStack.length > 0) {
      const newStack = [...cursorStack];
      newStack.pop(); // Remove current page cursor
      setCursorStack(newStack);

      // Fetch previous page (or first page if stack is empty)
      const prevCursor = newStack.length > 0 ? newStack[newStack.length - 1] : undefined;
      fetchJobs(prevCursor);
    }
  };

  // Fix failed modal handlers
  const openFixFailedModal = (job: JobRun) => {
    setFixFailedJob(job);
    setFixFailedMaxItems(200);
    setFixFailedModel('');
    setFixFailedPromptVersion('');
    setFixFailedDryRun(true);
    setFixFailedResult(null);
    setFixFailedError(null);
  };

  const closeFixFailedModal = () => {
    setFixFailedJob(null);
    setFixFailedResult(null);
    setFixFailedError(null);
  };

  const handleFixFailed = async () => {
    if (!session?.access_token || !fixFailedJob) return;

    setFixFailedLoading(true);
    setFixFailedError(null);
    setFixFailedResult(null);

    try {
      const body: FixFailedRequest = {
        dry_run: fixFailedDryRun,
        max_items: fixFailedMaxItems,
      };
      if (fixFailedModel.trim()) body.model = fixFailedModel.trim();
      if (fixFailedPromptVersion.trim()) body.prompt_version = fixFailedPromptVersion.trim();

      const result = await apiFetch<FixFailedResponse>(
        `/admin/jobs/${fixFailedJob.id}/fix-failed`,
        {
          method: 'POST',
          token: session.access_token,
          body,
        }
      );

      setFixFailedResult(result);

      // If not dry run and successful, navigate to new run
      if (!fixFailedDryRun && result.retry_job_run_id) {
        setTimeout(() => {
          navigate(`/admin/jobs/${result.retry_job_run_id}`);
        }, 1500);
      }
    } catch (err) {
      setFixFailedError(err instanceof Error ? err.message : 'Failed to fix failed items');
    } finally {
      setFixFailedLoading(false);
    }
  };

  // Review modal handlers
  const openReviewModal = (job: JobRun) => {
    setReviewJob(job);
    setReviewMaxItems(200);
    setReviewModel(ALLOWED_REVIEWER_MODELS[0].value);
    setReviewPromptVersion('');
    setReviewDryRun(true);
    setReviewResult(null);
    setReviewError(null);
  };

  const closeReviewModal = () => {
    setReviewJob(null);
    setReviewResult(null);
    setReviewError(null);
  };

  const handleStartReview = async () => {
    if (!session?.access_token || !reviewJob) return;

    setReviewLoading(true);
    setReviewError(null);
    setReviewResult(null);

    try {
      const body: StartReviewRequest = {
        dry_run: reviewDryRun,
        max_items: reviewMaxItems,
        model: reviewModel,
      };
      if (reviewPromptVersion.trim()) body.prompt_version = reviewPromptVersion.trim();

      const result = await apiFetch<StartReviewResponse>(
        `/admin/jobs/${reviewJob.id}/review/run`,
        {
          method: 'POST',
          token: session.access_token,
          body,
        }
      );

      setReviewResult(result);

      // If not dry run and successful, navigate to review detail
      if (!reviewDryRun && result.review_id) {
        setTimeout(() => {
          navigate(`/admin/reviews/${result.review_id}`);
        }, 1500);
      }
    } catch (err) {
      setReviewError(err instanceof Error ? err.message : 'Failed to start review');
    } finally {
      setReviewLoading(false);
    }
  };

  return (
    <AdminLayout
      title="Job Runs"
      description="View recent AI job runs and their results"
    >
      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex gap-2 flex-wrap">
          {JOB_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setJobType(opt.value)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                jobType === opt.value
                  ? 'bg-teal-500 text-white'
                  : 'bg-[#1A1F26] text-slate-300 hover:text-white'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => {
            setCursorStack([]);
            fetchJobs();
          }}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-300 bg-[#1A1F26] rounded-lg hover:text-white transition-colors ml-auto"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-4">
          <div className="flex items-center gap-2 text-red-400">
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Results */}
      {response && (
        <div className="p-4 bg-[#1A1F26] rounded-lg border border-white/[0.08]">
          {response.jobs.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No job runs found</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-400 border-b border-white/[0.08]">
                      <th className="pb-2 pr-4">Status</th>
                      <th className="pb-2 pr-4">Job Type</th>
                      {/* Show tag_key column when filtering by microtag */}
                      {jobType === 'ai_microtag_v1' && (
                        <th className="pb-2 pr-4">Tag Key</th>
                      )}
                      <th className="pb-2 pr-4">Progress</th>
                      <th className="pb-2 pr-4">Review</th>
                      <th className="pb-2 pr-4">Duration</th>
                      <th className="pb-2 pr-4">Started</th>
                      <th className="pb-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {response.jobs.map((job) => {
                      const uiStatus = deriveStatus(job);
                      const tagKey = job.job_type === 'ai_microtag_v1' && job.selector
                        ? (job.selector.tag_key as string | undefined)
                        : undefined;

                      return (
                        <tr
                          key={job.id}
                          className="border-b border-white/[0.04] hover:bg-white/[0.02]"
                        >
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-2">
                              {getStatusIcon(uiStatus)}
                              <span className={getStatusColor(uiStatus)}>
                                {getStatusLabel(uiStatus)}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 pr-4 text-slate-300">
                            {formatJobType(job.job_type)}
                          </td>
                          {/* Show tag_key when filtering by microtag */}
                          {jobType === 'ai_microtag_v1' && (
                            <td className="py-3 pr-4">
                              {tagKey ? (
                                <code className="text-xs text-teal-400 bg-[#0F1419] px-1.5 py-0.5 rounded">
                                  {tagKey}
                                </code>
                              ) : (
                                <span className="text-slate-500">-</span>
                              )}
                            </td>
                          )}
                          <td className="py-3 pr-4">
                            {(() => {
                              // Use live progress when available (running or inconsistent rollups), else static data
                              const liveProgress = progressByJobId[job.id];
                              const done = liveProgress ? liveProgress.done : (job.items_succeeded + job.items_failed);
                              const total = liveProgress ? liveProgress.items_attempted : job.items_attempted;
                              const failed = liveProgress ? liveProgress.counts.failed : job.items_failed;
                              const processing = liveProgress?.counts.processing ?? 0;

                              return (
                                <div className="flex items-center gap-2">
                                  <span className="text-green-400">{done}</span>
                                  <span className="text-slate-500">/</span>
                                  <span className="text-slate-300">{total}</span>
                                  {processing > 0 && (
                                    <span className="text-blue-400 text-xs">
                                      ({processing} processing)
                                    </span>
                                  )}
                                  {failed > 0 && (
                                    <span className="text-red-400 text-xs">
                                      ({failed} failed)
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-2">
                              {getReviewStatusBadge(job.review_status)}
                              {job.latest_review && (
                                <Link
                                  to={`/admin/reviews/${job.latest_review.id}`}
                                  className="text-slate-400 hover:text-teal-400 transition-colors"
                                  title="View review"
                                >
                                  <Eye size={14} />
                                </Link>
                              )}
                            </div>
                          </td>
                          <td className="py-3 pr-4 text-slate-400">
                            {formatDuration(job.started_at, job.finished_at)}
                          </td>
                          <td className="py-3 pr-4 text-slate-400">
                            {new Date(job.started_at).toLocaleDateString()}{' '}
                            {new Date(job.started_at).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </td>
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              {/* Fix failed button - only show for finished runs with errors */}
                              {uiStatus === 'has_errors' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openFixFailedModal(job);
                                  }}
                                  className="flex items-center gap-1 px-2 py-1 text-xs text-orange-400 bg-orange-500/10 rounded hover:bg-orange-500/20 transition-colors"
                                  title="Retry failed items"
                                >
                                  <Wrench size={12} />
                                  Fix
                                </button>
                              )}
                              {/* Review button - show for finished jobs (can review multiple times) */}
                              {job.finished_at && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openReviewModal(job);
                                  }}
                                  className="flex items-center gap-1 px-2 py-1 text-xs text-purple-400 bg-purple-500/10 rounded hover:bg-purple-500/20 transition-colors"
                                  title="Review AI outputs"
                                >
                                  <Search size={12} />
                                  Review
                                </button>
                              )}
                              <Link
                                to={`/admin/jobs/${job.id}`}
                                className="flex items-center gap-1 text-teal-400 hover:text-teal-300"
                              >
                                View
                                <ChevronRight size={14} />
                              </Link>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/[0.08]">
                <p className="text-sm text-slate-400">
                  Page {cursorStack.length + 1}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={cursorStack.length === 0 || loading}
                    className="px-3 py-1.5 text-sm text-slate-300 bg-[#0F1419] rounded-lg hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={!response.next_cursor || loading}
                    className="px-3 py-1.5 text-sm text-slate-300 bg-[#0F1419] rounded-lg hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Fix Failed Modal */}
      {fixFailedJob && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#1A1F26] rounded-lg border border-white/[0.08] w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/[0.08]">
              <h3 className="text-lg font-semibold text-white">
                Fix Failed Items
              </h3>
              <button
                onClick={closeFixFailedModal}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4">
              {/* Job info */}
              <div className="text-sm text-slate-400">
                <p>Job: <span className="text-white">{formatJobType(fixFailedJob.job_type)}</span></p>
                <p>Failed items: <span className="text-red-400">{fixFailedJob.items_failed}</span></p>
              </div>

              {/* Form inputs */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Max Items</label>
                  <input
                    type="number"
                    value={fixFailedMaxItems}
                    onChange={(e) => setFixFailedMaxItems(Math.min(2000, Math.max(1, parseInt(e.target.value) || 1)))}
                    min={1}
                    max={2000}
                    className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.08] rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Model Override (optional)</label>
                  <select
                    value={fixFailedModel}
                    onChange={(e) => setFixFailedModel(e.target.value)}
                    className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.08] rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
                  >
                    {ALLOWED_TAGGING_MODELS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Prompt Version Override (optional)</label>
                  <input
                    type="text"
                    value={fixFailedPromptVersion}
                    onChange={(e) => setFixFailedPromptVersion(e.target.value)}
                    placeholder="e.g., v1-opus-retry"
                    className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.08] rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="dryRun"
                    checked={fixFailedDryRun}
                    onChange={(e) => setFixFailedDryRun(e.target.checked)}
                    className="w-4 h-4 rounded bg-[#0F1419] border-white/[0.08] text-teal-500 focus:ring-teal-500"
                  />
                  <label htmlFor="dryRun" className="text-sm text-slate-300">
                    Dry run (preview only)
                  </label>
                </div>
              </div>

              {/* Error display */}
              {fixFailedError && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center gap-2 text-red-400 text-sm">
                    <AlertTriangle size={14} />
                    <span>{fixFailedError}</span>
                  </div>
                </div>
              )}

              {/* Result display */}
              {fixFailedResult && (
                <div className={`p-3 rounded-lg border ${
                  fixFailedResult.dry_run
                    ? 'bg-blue-500/10 border-blue-500/20'
                    : 'bg-green-500/10 border-green-500/20'
                }`}>
                  <p className={`text-sm font-medium ${
                    fixFailedResult.dry_run ? 'text-blue-400' : 'text-green-400'
                  }`}>
                    {fixFailedResult.dry_run ? 'Preview' : 'Retry Run Created'}
                  </p>
                  <p className="text-sm text-slate-300 mt-1">
                    Items selected: {fixFailedResult.items_selected}
                  </p>
                  {!fixFailedResult.dry_run && fixFailedResult.retry_job_run_id && (
                    <p className="text-sm text-slate-300">
                      Batches enqueued: {fixFailedResult.batches_enqueued}
                    </p>
                  )}
                  {fixFailedResult.next_cursor && (
                    <p className="text-xs text-slate-400 mt-1">
                      More items available (use cursor to continue)
                    </p>
                  )}
                  {fixFailedResult.items_selected === 0 && (
                    <p className="text-sm text-slate-400 mt-1">
                      {fixFailedResult.message || 'No failed items match the criteria'}
                    </p>
                  )}
                  {!fixFailedResult.dry_run && fixFailedResult.retry_job_run_id && (
                    <p className="text-xs text-teal-400 mt-2">
                      Redirecting to new run...
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 p-4 border-t border-white/[0.08]">
              <button
                onClick={closeFixFailedModal}
                className="px-4 py-2 text-sm text-slate-300 bg-[#0F1419] rounded-lg hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleFixFailed}
                disabled={fixFailedLoading || (!fixFailedDryRun && !!fixFailedResult?.retry_job_run_id)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  fixFailedDryRun
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
              >
                {fixFailedLoading ? (
                  <RefreshCw size={14} className="animate-spin" />
                ) : fixFailedDryRun ? (
                  'Preview'
                ) : (
                  'Execute Retry'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewJob && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#1A1F26] rounded-lg border border-white/[0.08] w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/[0.08]">
              <h3 className="text-lg font-semibold text-white">
                Review AI Outputs
              </h3>
              <button
                onClick={closeReviewModal}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4">
              {/* Job info */}
              <div className="text-sm text-slate-400">
                <p>Job: <span className="text-white">{formatJobType(reviewJob.job_type)}</span></p>
                <p>Items to review: <span className="text-white">{reviewJob.items_succeeded}</span> succeeded items</p>
                {reviewJob.review_status !== 'none' && (
                  <p>Previous review: {getReviewStatusBadge(reviewJob.review_status)}</p>
                )}
              </div>

              {/* Form inputs */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Max Items</label>
                  <input
                    type="number"
                    value={reviewMaxItems}
                    onChange={(e) => setReviewMaxItems(Math.min(500, Math.max(1, parseInt(e.target.value) || 1)))}
                    min={1}
                    max={500}
                    className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.08] rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Reviewer Model</label>
                  <select
                    value={reviewModel}
                    onChange={(e) => setReviewModel(e.target.value)}
                    className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.08] rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
                  >
                    {ALLOWED_REVIEWER_MODELS.map((model) => (
                      <option key={model.value} value={model.value}>
                        {model.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Prompt Version Override (optional)</label>
                  <input
                    type="text"
                    value={reviewPromptVersion}
                    onChange={(e) => setReviewPromptVersion(e.target.value)}
                    placeholder="e.g., review_v2"
                    className="w-full px-3 py-2 bg-[#0F1419] border border-white/[0.08] rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="reviewDryRun"
                    checked={reviewDryRun}
                    onChange={(e) => setReviewDryRun(e.target.checked)}
                    className="w-4 h-4 rounded bg-[#0F1419] border-white/[0.08] text-teal-500 focus:ring-teal-500"
                  />
                  <label htmlFor="reviewDryRun" className="text-sm text-slate-300">
                    Dry run (preview only)
                  </label>
                </div>
              </div>

              {/* Error display */}
              {reviewError && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center gap-2 text-red-400 text-sm">
                    <AlertTriangle size={14} />
                    <span>{reviewError}</span>
                  </div>
                </div>
              )}

              {/* Result display */}
              {reviewResult && (
                <div className={`p-3 rounded-lg border ${
                  reviewResult.dry_run
                    ? 'bg-blue-500/10 border-blue-500/20'
                    : 'bg-purple-500/10 border-purple-500/20'
                }`}>
                  <p className={`text-sm font-medium ${
                    reviewResult.dry_run ? 'text-blue-400' : 'text-purple-400'
                  }`}>
                    {reviewResult.dry_run ? 'Preview' : 'Review Run Started'}
                  </p>
                  <p className="text-sm text-slate-300 mt-1">
                    Items selected: {reviewResult.items_selected}
                  </p>
                  {!reviewResult.dry_run && reviewResult.review_id && (
                    <p className="text-sm text-slate-300">
                      Batches enqueued: {reviewResult.batches_enqueued}
                    </p>
                  )}
                  {reviewResult.next_cursor && (
                    <p className="text-xs text-slate-400 mt-1">
                      More items available (use cursor to continue)
                    </p>
                  )}
                  {reviewResult.items_selected === 0 && (
                    <p className="text-sm text-slate-400 mt-1">
                      {reviewResult.message || 'No succeeded items found to review'}
                    </p>
                  )}
                  {!reviewResult.dry_run && reviewResult.review_id && (
                    <p className="text-xs text-teal-400 mt-2">
                      Redirecting to review page...
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 p-4 border-t border-white/[0.08]">
              <button
                onClick={closeReviewModal}
                className="px-4 py-2 text-sm text-slate-300 bg-[#0F1419] rounded-lg hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStartReview}
                disabled={reviewLoading || (!reviewDryRun && !!reviewResult?.review_id)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  reviewDryRun
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-purple-500 text-white hover:bg-purple-600'
                }`}
              >
                {reviewLoading ? (
                  <RefreshCw size={14} className="animate-spin" />
                ) : reviewDryRun ? (
                  'Preview'
                ) : (
                  'Start Review'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
